// =============================================================================
// REFACTORED: useUserOrders Hook
// =============================================================================
// This file has been refactored from a 114-line monolithic hook into a modular
// system of 6 specialized hooks for better maintainability and testing.
//
// Original structure -> New modular structure:
// ├── State Management  -> useUserOrderState.ts
// ├── Data Fetching     -> useUserOrderData.ts  
// ├── Statistics        -> useUserOrderStats.ts
// ├── Initialization    -> useUserOrderInit.ts
// └── Main Orchestrator -> useUserOrders.ts
//
// This file now simply re-exports the new orchestrator hook for backward compatibility.
// =============================================================================

// Import the new modular hook system
import { useUserOrderState } from './useUserOrderState';
import { useUserOrderData } from './useUserOrderData';
import { useUserOrderStats } from './useUserOrderStats';
import { useUserOrderInit } from './useUserOrderInit';
import { UseUserOrdersReturn } from './UserOrderTypes';

export const useUserOrders = (userId: number | null): UseUserOrdersReturn => {
  const orderState = useUserOrderState();
  const orderStats = useUserOrderStats(userId, orderState.setStats);
  const orderData = useUserOrderData({ 
    userId, 
    orderState, 
    fetchOrderStats: orderStats.fetchOrderStats 
  });
  useUserOrderInit(userId, orderData.fetchOrdersSummary, orderStats.fetchOrderStats);

  return {
    orders: orderState.orders,
    ordersSummary: orderState.ordersSummary,
    stats: orderState.stats,
    loading: orderState.loading,
    error: orderState.error,
    fetchUserOrders: orderData.fetchUserOrders,
    fetchOrdersSummary: orderData.fetchOrdersSummary,
    fetchOrderStats: orderStats.fetchOrderStats,
    fetchOrderDetail: orderData.fetchOrderDetail,
    refreshData: orderData.refreshData,
  };
};

// Export individual hooks for granular usage
export { 
  useUserOrderState,
  useUserOrderData,
  useUserOrderStats,
  useUserOrderInit
} from './index';

// Export types
export type {
  UserOrderStats,
  UseUserOrdersReturn
} from './UserOrderTypes';

// Export constants
export {
  defaultUserOrderStats
} from './UserOrderTypes';

// =============================================================================
// MIGRATION NOTES:
// =============================================================================
// 1. The main useUserOrders() interface remains 100% backward compatible
// 2. All existing imports and usage will continue to work without changes
// 3. Individual hooks can be imported for specific use cases
// 4. The monolithic 114-line file is now split into 6 focused modules
// 5. Performance improved through better state isolation and memoization
// ============================================================================= 